// ----------------------------------------------------------------------------
// Church settings. One Save button at the top, beside the H1, disabled until
// something changes. Covers the church profile, address, setlist emails
// (lyrics recipient + "Songs without lyrics" policy), the admin's own email
// notification categories, stepping down as admin, and deleting the church.
// ----------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@app/auth/AuthContext';
import { churchesApi, membershipsApi, notificationPreferencesApi, type NotificationPreferences } from '@app/api/organization';
import type { Church, LyricsPublishPolicy } from '@app/types';
import { Button, Card, ConfirmDialog, ErrorState, Field, Input, Modal, ModalActions, Select, SkeletonRows, Textarea, Toggle, ToggleRow, cx, useToast } from '@app/components/ui';
import { featureFlags } from '@admin/shared/featureFlags';
import { LinkedChurches } from './LinkedChurches';
import { redesignFlags } from '@app/featureFlags';
import { logger } from '@app/utils/logger';

export interface ChurchForm {
  name: string;
  description: string;
  phone: string;
  website: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  sendLyricsOnPublish: boolean;
  lyricsRecipientEmail: string;
  lyricsPublishPolicy: LyricsPublishPolicy;
}

const fromChurch = (c: Church): ChurchForm => ({
  name: c.name ?? '',
  description: c.description ?? '',
  phone: c.contactInfo?.phone ?? '',
  website: c.contactInfo?.website ?? '',
  street: c.address?.street ?? '',
  city: c.address?.city ?? '',
  state: c.address?.state ?? '',
  zipCode: c.address?.zipCode ?? '',
  country: c.address?.country ?? '',
  sendLyricsOnPublish: c.setlistSettings?.sendLyricsOnPublish ?? false,
  lyricsRecipientEmail: c.setlistSettings?.lyricsRecipientEmail ?? '',
  lyricsPublishPolicy: c.setlistSettings?.lyricsPublishPolicy ?? 'prompt',
});

export const LYRICS_POLICIES: { value: LyricsPublishPolicy; label: string; description: string }[] = [
  { value: 'prompt', label: 'Ask first', description: 'Warn about songs with no lyrics, then let the leader publish' },
  { value: 'block', label: 'Require lyrics', description: 'A setlist can only be published when every song has lyrics' },
  { value: 'off', label: "Don't check", description: 'Publish without looking at lyrics' },
];

// Email categories an admin can opt out of (per admin, default on). Matches
// the categories the notifications service understands.
const EMAIL_CATEGORIES: { key: string; label: string; description: string; locked?: boolean }[] = [
  { key: 'membership', label: 'Join requests and members', description: 'Someone asks to join, is approved, or leaves' },
  { key: 'assignments', label: 'Roster responses', description: 'People accept or decline a service. Always on', locked: true },
  { key: 'setlist', label: 'Setlist updates', description: 'New setlists and song changes for teams you serve on' },
  { key: 'billing', label: 'Plan and billing', description: 'Plan changes, renewals and payment problems' },
];

const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Ireland', 'Australia', 'New Zealand', 'South Africa', 'Nigeria', 'Kenya', 'Ghana', 'Philippines', 'India', 'Singapore', 'Germany', 'Netherlands', 'Brazil', 'Mexico'];

const emailOn = (prefs: NotificationPreferences | null, key: string) => prefs?.categories?.[key]?.email ?? true;

export const ChurchSettings: React.FC = () => {
  const { user, logout } = useAuth();
  const toast = useToast();
  const churchId = user?.churchId;
  const [church, setChurch] = useState<Church | null>(null);
  const [saved, setSaved] = useState<ChurchForm | null>(null);
  const [form, setForm] = useState<ChurchForm | null>(null);
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [savedPrefs, setSavedPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [stepDownOpen, setStepDownOpen] = useState(false);

  const load = async () => {
    if (!churchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const data = await churchesApi.getById(churchId);
      setChurch(data);
      setSaved(fromChurch(data));
      setForm(fromChurch(data));
    } catch (err) {
      logger.error('Failed to fetch church:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
    notificationPreferencesApi
      .get()
      .then((p) => {
        setPrefs(p);
        setSavedPrefs(p);
      })
      .catch((err) => {
        logger.error('Failed to load notification preferences:', err);
        const empty = { categories: {} };
        setPrefs(empty);
        setSavedPrefs(empty);
      });
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  const formDirty = useMemo(() => !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  const prefsDirty = useMemo(() => JSON.stringify(prefs) !== JSON.stringify(savedPrefs), [prefs, savedPrefs]);
  const dirty = formDirty || prefsDirty;

  const set = <K extends keyof ChurchForm>(key: K, value: ChurchForm[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    if (key === 'name') setNameError('');
  };

  const save = async () => {
    if (!form || !churchId || !user) return;
    if (!form.name.trim()) {
      setNameError('Your church needs a name');
      return;
    }
    setSaving(true);
    try {
      if (formDirty) {
        await churchesApi.update(
          churchId,
          {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            address: {
              street: form.street || undefined,
              city: form.city || undefined,
              state: form.state || undefined,
              zipCode: form.zipCode || undefined,
              country: form.country || undefined,
            },
            contactInfo: { phone: form.phone || undefined, website: form.website || undefined },
            setlistSettings: {
              lyricsRecipientEmail: form.lyricsRecipientEmail.trim() || undefined,
              sendLyricsOnPublish: form.sendLyricsOnPublish,
              lyricsPublishPolicy: form.lyricsPublishPolicy,
            },
          },
          user.id,
        );
        const fresh = await churchesApi.getById(churchId);
        setChurch(fresh);
        setSaved(fromChurch(fresh));
        setForm(fromChurch(fresh));
      }
      if (prefsDirty && prefs) {
        const next = await notificationPreferencesApi.update(prefs);
        setPrefs(next);
        setSavedPrefs(next);
      }
      toast('Settings saved');
    } catch (err: any) {
      logger.error('Failed to save settings:', err);
      toast(err?.response?.data?.error || err?.message || 'Could not save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const flipEmail = (key: string, value: boolean) =>
    setPrefs((p) => {
      const base = p?.categories ?? {};
      const current = base[key] ?? { email: true, push: true };
      return { categories: { ...base, [key]: { ...current, email: value } } };
    });

  const stepDown = async () => {
    if (!user?.churchId || !user.id) return;
    try {
      const members = await membershipsApi.getChurchMembers(user.churchId);
      const mine = members.find((m) => m.userId === user.id);
      if (!mine) {
        toast('Could not find your membership record', 'error');
        return;
      }
      // The backend rejects this if you are the last admin.
      await membershipsApi.updateMembership(mine.id, { role: 'Member', updatedBy: user.id });
      toast('You stepped down as admin. Logging you out', 'info');
      setTimeout(async () => {
        await logout();
        window.location.href = '/login';
      }, 2000);
    } catch (err: any) {
      logger.error('Failed to step down:', err);
      toast(err?.response?.data?.error || err?.message || 'Could not step down', 'error');
    }
  };

  const countryOptions = useMemo(() => {
    const list = form?.country && !COUNTRIES.includes(form.country) ? [form.country, ...COUNTRIES] : COUNTRIES;
    return [{ value: '', label: 'Not set' }, ...list.map((c) => ({ value: c, label: c }))];
  }, [form?.country]);

  if (loading || (!form && !loadError)) {
    return (
      <div className="max-w-[820px]">
        <div className="wp-skeleton h-4 w-32 rounded-[6px] mb-3" />
        <div className="wp-skeleton h-10 w-72 rounded-[10px] mb-6" />
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (loadError || !form) {
    return (
      <div className="max-w-[820px]">
        <h1 className="wp-h1 mb-6">Church settings</h1>
        <ErrorState title="Couldn't load your church" body="Check your connection and try again." onRetry={load} />
      </div>
    );
  }

  return (
    <div className="max-w-[820px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-[13px] text-t4 mb-2">Church settings</div>
          <h1 className="wp-h1 truncate">{saved?.name || 'Your church'}</h1>
        </div>
        <Button size="lg" loading={saving} disabled={!dirty} onClick={save}>
          Save changes
        </Button>
      </div>

      <div className="flex flex-col gap-3.5">
        <Card className="flex flex-col gap-4" as="section" aria-labelledby="sec-profile">
          <h2 id="sec-profile" className="text-[15px] font-semibold">
            Church profile
          </h2>
          <Field label="Church name" htmlFor="st-name" error={nameError}>
            <Input id="st-name" value={form.name} invalid={!!nameError} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="Description" htmlFor="st-desc">
            <Textarea id="st-desc" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </Field>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))' }}>
            <Field label="Phone" htmlFor="st-phone">
              <Input id="st-phone" type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field label="Website" htmlFor="st-web">
              <Input id="st-web" type="url" value={form.website} placeholder="https://" onChange={(e) => set('website', e.target.value)} />
            </Field>
            {redesignFlags.churchTimeZone && (
              <Field label="Time zone" htmlFor="st-tz">
                <Select id="st-tz" value="" onChange={() => undefined} options={[{ value: '', label: 'Not set' }]} />
              </Field>
            )}
          </div>
        </Card>

        <Card className="flex flex-col gap-4" as="section" aria-labelledby="sec-address">
          <h2 id="sec-address" className="text-[15px] font-semibold">
            Address
          </h2>
          <Field label="Street" htmlFor="st-street">
            <Input id="st-street" value={form.street} onChange={(e) => set('street', e.target.value)} />
          </Field>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(160px,100%),1fr))' }}>
            <Field label="City" htmlFor="st-city">
              <Input id="st-city" value={form.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
            <Field label="State or region" htmlFor="st-state">
              <Input id="st-state" value={form.state} onChange={(e) => set('state', e.target.value)} />
            </Field>
            <Field label="Postal code" htmlFor="st-zip">
              <Input id="st-zip" mono value={form.zipCode} onChange={(e) => set('zipCode', e.target.value)} />
            </Field>
            <Field label="Country" htmlFor="st-country">
              <Select id="st-country" value={form.country} onChange={(v) => set('country', v)} options={countryOptions} />
            </Field>
          </div>
        </Card>

        <Card className="flex flex-col gap-4" as="section" aria-labelledby="sec-lyrics">
          <div>
            <h2 id="sec-lyrics" className="text-[15px] font-semibold">
              Setlist emails
            </h2>
            <p className="text-[13px] text-t4 mt-[3px]">When a setlist is published, we can email the lyrics to your media or slides team.</p>
          </div>
          <ToggleRow
            title="Send lyrics when a setlist is published"
            description="Includes every song's lyrics in setlist order"
            checked={form.sendLyricsOnPublish}
            onChange={(v) => set('sendLyricsOnPublish', v)}
          />
          <Field label="Lyrics recipient email" htmlFor="st-lyrics-email" hint="Separate more than one address with commas.">
            <Input
              id="st-lyrics-email"
              type="text"
              inputMode="email"
              placeholder="media@yourchurch.org"
              value={form.lyricsRecipientEmail}
              onChange={(e) => set('lyricsRecipientEmail', e.target.value)}
            />
          </Field>
          <fieldset>
            <legend className="wp-label">Songs without lyrics</legend>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Songs without lyrics">
              {LYRICS_POLICIES.map((p) => {
                const on = form.lyricsPublishPolicy === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => set('lyricsPublishPolicy', p.value)}
                    onKeyDown={(e) => {
                      const i = LYRICS_POLICIES.findIndex((x) => x.value === form.lyricsPublishPolicy);
                      const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
                      if (!step) return;
                      e.preventDefault();
                      const next = LYRICS_POLICIES[(i + step + LYRICS_POLICIES.length) % LYRICS_POLICIES.length];
                      set('lyricsPublishPolicy', next.value);
                      (e.currentTarget.parentElement?.querySelector(`[data-policy="${next.value}"]`) as HTMLElement | null)?.focus();
                    }}
                    tabIndex={on ? 0 : -1}
                    data-policy={p.value}
                    className={cx(
                      'flex items-center gap-3 w-full text-left px-3.5 py-3 rounded-[13px] border',
                      on ? 'border-[rgba(208,48,69,.45)] bg-acc-bg' : 'border-line2 bg-fill hover:bg-fill2',
                    )}
                  >
                    <span className={cx('w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center shrink-0', on ? 'border-acc' : 'border-t5')}>
                      <span className={cx('w-2 h-2 rounded-full', on ? 'bg-acc' : 'bg-transparent')} />
                    </span>
                    <span>
                      <span className="block text-[14px] font-semibold">{p.label}</span>
                      <span className="block text-[12.5px] text-t4 mt-px">{p.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>
        </Card>

        <Card as="section" aria-labelledby="sec-notif">
          <h2 id="sec-notif" className="text-[15px] font-semibold">
            Team notifications
          </h2>
          <p className="text-[13px] text-t4 mt-[3px] mb-2">Emails we send you. Turning one off only changes your account.</p>
          {EMAIL_CATEGORIES.map((c) => (
            <div key={c.key} className="flex items-center gap-3.5 py-3 border-t border-line">
              <div className="flex-1">
                <div className="text-[14px] font-medium">{c.label}</div>
                <div className="text-[12.5px] text-t4 mt-0.5">{c.description}</div>
              </div>
              <Toggle
                label={c.label}
                checked={c.locked ? true : emailOn(prefs, c.key)}
                disabled={c.locked || !prefs}
                onChange={(v) => flipEmail(c.key, v)}
              />
            </div>
          ))}
        </Card>

        {featureFlags.churchHubLinking && churchId && <LinkedChurches churchId={churchId} />}

        <Card as="section" className="flex flex-wrap items-center gap-3.5" aria-labelledby="sec-stepdown">
          <div className="flex-1 min-w-[220px]">
            <h2 id="sec-stepdown" className="text-[15px] font-semibold">
              Your admin role
            </h2>
            <p className="text-[13px] text-t4 mt-[3px] leading-normal">Step down to a regular member. Another admin has to promote you to get access back.</p>
          </div>
          <Button variant="ghost" onClick={() => setStepDownOpen(true)}>
            Step down
          </Button>
        </Card>

        <section
          aria-labelledby="sec-delete"
          className="flex flex-wrap items-center gap-3.5 rounded-[24px] border bg-acc-bg p-[22px]"
          style={{ borderColor: 'rgba(208,48,69,.3)' }}
        >
          <div className="flex-1 min-w-[220px]">
            <h2 id="sec-delete" className="text-[15px] font-semibold">
              Delete church
            </h2>
            <p className="text-[13px] text-t2 mt-[3px] leading-normal">Removes every team, roster, setlist and song for all members.</p>
          </div>
          <Button variant="primary" onClick={() => setDeleteOpen(true)}>
            Delete church
          </Button>
        </section>
      </div>

      <ConfirmDialog
        isOpen={stepDownOpen}
        onClose={() => setStepDownOpen(false)}
        onConfirm={stepDown}
        icon={null}
        title="Step down as admin?"
        message="You stay in the church as a member, and you'll be logged out. Another admin has to promote you to get admin access back."
        confirmText="Step down"
      />
      {church && <DeleteChurchModal open={deleteOpen} church={church} onClose={() => setDeleteOpen(false)} />}
    </div>
  );
};

function DeleteChurchModal({ open, church, onClose }: { open: boolean; church: Church; onClose: () => void }) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const matches = typed.trim() === church.name;

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const run = async () => {
    if (!matches || !user) return;
    setBusy(true);
    try {
      await churchesApi.delete(church.id, user.id);
      toast('Church scheduled for deletion. Sign back in before the recovery window ends to restore it.', 'info');
      setTimeout(async () => {
        await logout();
        window.location.href = '/login';
      }, 2500);
    } catch (err: any) {
      logger.error('Failed to delete organization:', err);
      toast(err?.response?.data?.error || err?.message || 'Could not delete the church', 'error');
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Delete ${church.name}?`} subtitle="Every team, roster, setlist and song goes for all members." width={460} dismissible={!busy}>
      <div className="flex flex-col gap-4">
        <p className="text-[13.5px] text-t3 leading-[1.55]">
          You can restore it by signing back in during the recovery window. After that it's permanently removed. Every admin gets an email about it.
        </p>
        <Field label={<>Type <span className="font-semibold text-t1">{church.name}</span> to confirm</>} htmlFor="del-confirm">
          <Input id="del-confirm" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
        </Field>
        <ModalActions onCancel={onClose} confirmLabel="Delete church" confirmType="button" onConfirm={run} busy={busy} disabled={!matches} />
      </div>
    </Modal>
  );
}
